package gr.tech.GRAttestation.pages.admin.activity

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.silk.components.icons.fa.FaFilter
import com.varabyte.kobweb.silk.components.icons.fa.FaRotateLeft
import gr.tech.GRAttestation.components.PaginationControls
import gr.tech.GRAttestation.components.layouts.AdminPageLayout
import gr.tech.GRAttestation.components.widgets.AlertManager
import gr.tech.GRAttestation.components.widgets.NavBreadcrumb
import gr.tech.GRAttestation.components.widgets.UserAlert
import gr.tech.GRAttestation.infrastructure.api.adminFetchAllActivity
import gr.tech.GRAttestation.models.activity.ActivityDTO
import gr.tech.GRAttestation.models.tables.BasicTableFilters
import gr.tech.GRAttestation.sections.tables.AdminActivityTable
import gr.tech.GRAttestation.styles.grstyles.Theme
import gr.tech.GRAttestation.util.Constants
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@Page()
@Composable
fun AdminActivityPage() {
    AdminPageLayout() { context ->
        AdminActivityScreen(context)
    }
}

@Composable
fun AdminActivityScreen(context: PageContext) {
    val scope = rememberCoroutineScope()
    var triggerLaunchedEffect by remember { mutableStateOf(false) }
    val alertManager = remember { AlertManager() }
    alertManager.DisplayAlerts()

    val activities = remember { mutableStateListOf<ActivityDTO>() }
    var activityTotalCount by remember { mutableStateOf(0) }
    val pageSize = Constants.ADMIN_TABLE_SIZE_DEFAULT
    val totalPages = (activityTotalCount + pageSize - 1) / pageSize
    var currentPage by remember { mutableStateOf(1) }

    // Filter Data
    var basicFilters by remember { mutableStateOf(BasicTableFilters()) }
    var selectedDateColumnLabel by remember { mutableStateOf("Created At") }

    LaunchedEffect(Unit, triggerLaunchedEffect, currentPage, basicFilters) {
        adminFetchAllActivity(
            pageNumber = currentPage,
            pageSize = pageSize,
            basicFilters = basicFilters,
            onResponse = { response ->
                if (response.success && response.data != null) {
                    activities.clear()
                    activities.addAll(response.data.items)
                    activityTotalCount = response.data.totalCount
                    if (activities.isEmpty()) { currentPage = 1 }
                } else {
                    alertManager.addAlert(UserAlert(message = "Error loading activity!", status = 0))
                }
            },
            onError = {
                console.log("Error with adminFetchAllActivity(): $it")
                alertManager.addAlert(UserAlert(message = "Error loading activity!", status = 0))
            }
        )
    }

    Column(
        modifier = Modifier
            .padding(24.px)
            .backgroundColor(Theme.HalfWhite.rgb)
            .fillMaxSize()
    ) {
        NavBreadcrumb(ctx = context)

        // Top Row Buttons
        Row(
            modifier = Modifier.fillMaxWidth().margin(bottom = 16.px),
            horizontalArrangement = Arrangement.Start,
            verticalAlignment = Alignment.CenterVertically
        )
        {
            Button(
                attrs = Modifier
                    .classNames("btn", "btn-primary")
                    .onClick {
                        scope.launch {
                            adminFetchAllActivity(
                                pageNumber = currentPage,
                                pageSize = pageSize,
                                basicFilters = basicFilters,
                                onResponse = { response ->
                                    if (response.success && response.data != null) {
                                        activities.clear()
                                        activities.addAll(response.data.items)
                                        activityTotalCount = response.data.totalCount
                                    }
                                },
                                onError = { console.log(it) },
                            )
                        }
                    }
                    .margin(right = 16.px)
                    .toAttrs()
            ) {
                FaRotateLeft(modifier = Modifier.margin(right = 8.px))
                Text("Refresh")
            }

            Button(
                attrs = Modifier.classNames("btn", "btn-primary")
                    .margin(right = 16.px)
                    .onClick {}
                    .toAttrs {
                        attr("data-bs-toggle", "collapse")
                        attr("data-bs-target", "#adminActivityFilters")
                    }
            ) {
                FaFilter(modifier = Modifier.margin(right = 8.px))
                Text("Filters")
            }

            Spacer()

            PaginationControls(
                currentPage = currentPage,
                totalPages = totalPages,
                onPageSelected = { pageNumber ->
                    currentPage = pageNumber

                }
            )
        }

        // Filters Section
        Column(
            modifier = Modifier.id("adminActivityFilters").classNames("collapse")
                .margin(top = 8.px, bottom = 16.px)
                .padding(8.px)
                .backgroundColor(Colors.WhiteSmoke)
                .borderRadius(8.px)
                .fillMaxWidth()
        )
        {
            // Your Filters Row
            Row(
                modifier = Modifier
                    .fillMaxWidth().gap(16.px),
                horizontalArrangement = Arrangement.Start,
                verticalAlignment = Alignment.CenterVertically
            ) {

                // Dropdown for column
                Div(attrs = Modifier.classNames("dropdown").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", "btn-secondary", "dropdown-toggle")
                            .id("dateColumnDropdown")
                            .toAttrs {
                                attr("data-bs-toggle", "dropdown")
                            }
                    ) {
                        Text(selectedDateColumnLabel)
                    }
                    Ul(attrs = Modifier.classNames("dropdown-menu").toAttrs()) {
                        Li {
                            A(
                                href = "#",
                                attrs = Modifier.classNames("dropdown-item")
                                    .onClick {
                                        basicFilters = basicFilters.copy(
                                            dateColumn = "createdAt"
                                        )
                                        selectedDateColumnLabel = "Created At"
                                    }.toAttrs()
                            ) {
                                Text("Created At")
                            }
                        }
                    }
                }

                // ASCENDING / DESCENDING toggle
                Div(attrs = Modifier.classNames("btn-group").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (basicFilters.isAscending) "btn-primary" else "btn-outline-primary")
                            .onClick {
                                basicFilters = basicFilters.copy(
                                    isAscending = true
                                )
                            }
                            .toAttrs()
                    ) {
                        Text("ASCENDING")
                    }
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (!basicFilters.isAscending) "btn-primary" else "btn-outline-primary")
                            .onClick {
                                basicFilters = basicFilters.copy(
                                    isAscending = false
                                )
                            }
                            .toAttrs()
                    ) {
                        Text("DESCENDING")
                    }
                }

                // ASCENDING / DESCENDING toggle
                Div(attrs = Modifier.classNames("btn-group").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (basicFilters.isBefore) "btn-primary" else "btn-outline-primary")
                            .onClick { basicFilters = basicFilters.copy(isBefore = true) }
                            .toAttrs()
                    ) {
                        Text("BEFORE")
                    }
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (!basicFilters.isBefore) "btn-primary" else "btn-outline-primary")
                            .onClick { basicFilters = basicFilters.copy(isBefore = false) }
                            .toAttrs()
                    ) {
                        Text("AFTER")
                    }
                }

                // Date input
                Input(
                    type = InputType.Date,
                    attrs = Modifier
                        .classNames("form-control")
                        .width(200.px)
                        .toAttrs {
                            attr("value", basicFilters.dateString)
                            onChange {
                                console.log(it.value)
                                basicFilters = basicFilters.copy(
                                    dateString = it.value
                                )
                            }
                        }
                )

                Spacer()

                // Reset Filters button
                Button(
                    attrs = Modifier.classNames("btn", "btn-secondary")
                        .onClick {
                            basicFilters = BasicTableFilters()
                            selectedDateColumnLabel = "Created At"
                        }
                        .toAttrs {}
                ) {
                    FaRotateLeft(modifier = Modifier.margin(right = 8.px))
                    Text("Reset")
                }
            }

        }

        AdminActivityTable(
            activities = activities,
        )

        Row(
            modifier = Modifier.fillMaxWidth().margin(top = 16.px),
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.CenterVertically
        ) {
            PaginationControls(
                currentPage = currentPage,
                totalPages = totalPages,
                onPageSelected = { pageNumber ->
                    currentPage = pageNumber

                }
            )
        }

    }
}